import { ChangeDetectorRef, Component, OnInit, ViewChild, computed, model, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Table } from "../../s25-table/Table";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { Bind } from "../../../decorators/bind.decorator";
import { S25Util } from "../../../util/s25-util";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { UserprefService } from "../../../services/userpref.service";
import { PreferenceService } from "../../../services/preference.service";
import { SpecialDateService } from "../../../services/special.date.service";
import { SpecialDateCategories, SpecialDateRow } from "../../../pojo/SpecialDate";
import { formatCivilDateString, isUsFederalHolidayRow, specialDateRowKey } from "./special.date.util";
import { S25EditSpecialDateComponent } from "./s25.edit.special.date.component";

@TypeManagerDecorator("s25-ng-special-date-management-list")
@Component({
    selector: "s25-ng-special-date-management-list",
    template: `
        <div class="top">
            <h2 class="c-margin-bottom--single">Special Date</h2>
            <div class="header c-margin-bottom--single">
                <s25-ng-checkbox [value]="featureEnabled()" (valueChange)="onFeatureEnabledChange($event)">
                    Show special dates on calendars for this instance
                </s25-ng-checkbox>
            </div>
            <div class="header c-margin-bottom--single actions">
                <button class="aw-button aw-button--primary" (click)="onCreateClick()">Add Special Date</button>
            </div>
            <s25-ng-table
                class="c-margin-top--single"
                [dataSource]="tableData"
                [hasRefresh]="true"
                [hasFilter]="true"
                [showHeaderWhenNoData]="true"
                [columnSortable]="true"
            >
                <ng-template #tableHeaderLeft let-filter="filter">
                    <div class="filters">
                        <ng-container *ngTemplateOutlet="filter" />
                        <label class="filter">
                            Start Date:
                            <s25-ng-editable-date
                                [alwaysEditing]="true"
                                [(val)]="filterStartDate"
                                (valChange)="onFilterChange()"
                            ></s25-ng-editable-date>
                        </label>
                        <label class="filter">
                            End Date:
                            <s25-ng-editable-date
                                [alwaysEditing]="true"
                                [(val)]="filterEndDate"
                                (valChange)="onFilterChange()"
                            ></s25-ng-editable-date>
                        </label>
                        <s25-ng-checkbox [value]="showHolidays" (valueChange)="onShowHolidaysChange($event)">
                            Show Holidays
                        </s25-ng-checkbox>
                        @if (filterEndBeforeStart()) {
                            <div class="ngRed filter-date-error">End date must be on or after start date.</div>
                        }
                    </div>
                </ng-template>
            </s25-ng-table>

            <s25-ng-modal #editModal [title]="editTitle()" [size]="'xs'">
                <ng-template #s25ModalBody>
                    @if (editingRow()) {
                        <s25-ng-edit-special-date
                            [specialDate]="editingRow()"
                            (saved)="onEditSaved()"
                            (close)="closeEditModal()"
                        ></s25-ng-edit-special-date>
                    }
                </ng-template>
            </s25-ng-modal>

            <s25-ng-modal #deleteModal [title]="'Confirm Deletion'" [type]="'deny'" [size]="'xs'">
                Are you sure you want to delete this special date?
            </s25-ng-modal>
        </div>
    `,
    styles: `
        .filters {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75em;
            align-items: center;
        }
        .filter {
            display: flex;
            gap: 0.5em;
            align-items: center;
        }
        .filter-date-error {
            flex-basis: 100%;
        }
        .actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5em;
        }
    `,
    standalone: false,
})
export class S25SpecialDateManagementListComponent implements OnInit {
    tableData: Table.DataSource;
    readonly featureEnabled = signal(false);
    readonly editingRow = signal<SpecialDateRow | undefined>(undefined);
    readonly editTitle = signal("Add Special Date");

    showHolidays = true;
    readonly filterStartDate = model<string | Date>("");
    readonly filterEndDate = model<string | Date>("");
    readonly filterStartCivil = computed(() => S25Util.date.toCivilDateString(this.filterStartDate()) ?? "");
    readonly filterEndCivil = computed(() => S25Util.date.toCivilDateString(this.filterEndDate()) ?? "");
    readonly filterEndBeforeStart = computed(() => {
        const start = this.filterStartCivil();
        const end = this.filterEndCivil();
        if (!start || !end) {
            return false;
        }
        return end < start;
    });
    dateFormat: string;
    private rowsById = new Map<string, SpecialDateRow>();

    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;
    @ViewChild("editModal") editModal: S25ModalComponent;
    @ViewChild("deleteModal") deleteModal: S25ModalComponent;

    constructor(private changeDetector: ChangeDetectorRef) {
        this.setDefaultYearFilters();
        this.tableData = this.getTableConfig();
    }

    private setDefaultYearFilters() {
        const year = S25Util.date.currentDate().getFullYear();
        this.filterStartDate.set(`${year}-01-01`);
        this.filterEndDate.set(`${year}-12-31`);
    }

    async ngOnInit() {
        await this.loadFeaturePref();
        this.changeDetector.detectChanges();
    }

    private async loadDateFormat(): Promise<string> {
        const dateFormat = await UserprefService.getS25Dateformat();
        this.dateFormat = dateFormat;
        return dateFormat;
    }

    private async loadFeaturePref() {
        const prefs = await PreferenceService.getPreferences(["SpecialDatesEnabled"], "S");
        this.featureEnabled.set(S25Util.toBool(prefs?.SpecialDatesEnabled?.value));
    }

    async onFeatureEnabledChange(enabled: boolean) {
        this.featureEnabled.set(enabled);
        await PreferenceService.setPreference("SpecialDatesEnabled", S25Util.bool.toChar(enabled), "S");
    }

    getTableConfig(): Table.DataSource {
        return {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: [
                { id: "name", header: "Name" },
                { id: "category", header: "Category" },
                { id: "start", header: "Start" },
                { id: "end", header: "End" },
                { id: "showCalendar", header: "Show Calendar" },
                GenericTableButtonComponent.Column("Copy", this.onCopyClick, "outline"),
                GenericTableButtonComponent.Column("Edit", this.onEditClick, "outline"),
                GenericTableButtonComponent.Column("Delete", this.onDeleteClick, "danger--outline"),
            ],
        };
    }

    @Bind
    async getRows(): Promise<Table.DataSourceResponse> {
        const startDate = this.filterStartCivil();
        if (!startDate) {
            return { rows: [] };
        }
        const endDate = this.filterEndCivil();
        if (endDate && endDate < startDate) {
            return { rows: [] };
        }
        const [dateFormat, response] = await Promise.all([
            this.loadDateFormat(),
            SpecialDateService.list({
                startDate,
                endDate: endDate || undefined,
            }),
        ]);
        this.featureEnabled.set(S25Util.toBool(response.featureEnabled));
        let rows = response.dates ?? [];
        if (!this.showHolidays) {
            rows = rows.filter((row) => row.categoryCode !== SpecialDateCategories.HOLIDAY);
        }
        this.rowsById.clear();
        for (const row of rows) {
            const key = specialDateRowKey(row);
            if (key) {
                this.rowsById.set(key, row);
            }
        }
        return { rows: rows.map((row) => this.mapToRow(row, dateFormat)) };
    }

    private mapToRow(row: SpecialDateRow, dateFormat: string): Table.Row {
        const showOnCalendar = row.showOnCalendar !== false;
        const federal = isUsFederalHolidayRow(row);
        const cells: Table.Row["cells"] = {
            name: { text: row.name },
            category: { text: this.formatCategory(row.categoryCode) },
            start: {
                text: formatCivilDateString(row.startDate, dateFormat),
                sortValue: row.startDate,
            },
            end: {
                text: formatCivilDateString(row.endDate, dateFormat),
                sortValue: row.endDate,
            },
            showCalendar: { text: showOnCalendar ? "Yes" : "No" },
        };
        if (federal) {
            cells.copy = this.disabledFederalActionCell("Copy", "outline", "US federal holidays cannot be copied.");
            cells.delete = this.disabledFederalActionCell(
                "Delete",
                "danger--outline",
                "US federal holidays cannot be deleted.",
            );
        }
        return {
            id: specialDateRowKey(row),
            name: row.name,
            cells,
        };
    }

    private disabledFederalActionCell(
        label: string,
        type: "outline" | "danger--outline",
        disabledReason: string,
    ): Table.Cell<GenericTableButtonComponent> {
        return {
            component: GenericTableButtonComponent,
            inputs: {
                label,
                type,
                disabled: true,
                disabledReason,
            },
        };
    }

    private formatCategory(code: string): string {
        switch (code) {
            case SpecialDateCategories.HOLIDAY:
                return "Holiday";
            case SpecialDateCategories.CLOSURE:
                return "Closure";
            case SpecialDateCategories.ACADEMIC:
                return "Academic";
            default:
                return code ?? "";
        }
    }

    onFilterChange() {
        const startEmpty = !this.filterStartCivil();
        const endEmpty = !this.filterEndCivil();
        if (startEmpty || endEmpty) {
            this.setDefaultYearFilters();
        }
        if (this.filterEndBeforeStart()) {
            return;
        }
        void this.tableComponent?.refresh(true);
    }

    onShowHolidaysChange(showHolidays: boolean) {
        this.showHolidays = showHolidays;
        this.onFilterChange();
    }

    async onCreateClick() {
        this.editTitle.set("Add Special Date");
        this.editingRow.set({
            name: "",
            categoryCode: SpecialDateCategories.ACADEMIC,
            startDate: "",
            endDate: "",
            showOnCalendar: true,
        });
        await this.editModal.open();
    }

    @Bind
    async onCopyClick(tableRow: Table.Row) {
        const row = this.rowsById.get(String(tableRow.id));
        if (!row || isUsFederalHolidayRow(row)) {
            return;
        }
        this.editTitle.set(`Copy Special Date: ${row.name}`);
        this.editingRow.set({
            name: `${row.name} copy`,
            categoryCode: row.categoryCode,
            startDate: row.startDate,
            endDate: row.endDate,
            showOnCalendar: row.showOnCalendar,
        });
        await this.editModal.open();
    }

    @Bind
    async onEditClick(tableRow: Table.Row) {
        const row = this.rowsById.get(String(tableRow.id));
        if (!row) {
            return;
        }
        this.editTitle.set(`Edit ${row.name}`);
        this.editingRow.set({ ...row });
        await this.editModal.open();
    }

    @Bind
    async onDeleteClick(tableRow: Table.Row) {
        const row = this.rowsById.get(String(tableRow.id));
        if (!row || isUsFederalHolidayRow(row) || row.specialDateId == null) {
            return;
        }
        const confirmed = await this.deleteModal.open();
        if (!confirmed) {
            return;
        }
        await SpecialDateService.delete(row.specialDateId);
        await this.tableComponent.refresh(true);
    }

    async onEditSaved() {
        await this.tableComponent.refresh(true);
    }

    async closeEditModal() {
        await this.editModal.close();
    }
}
